import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kUntitledMatterLabel } from '@/matter/constants'
import { MattersRecord } from './slice'
import { MatterRead, MatterReadForTable, MatterType } from './schemas'
import { canCreateChat, canMoveChatToMatter, isActiveMatterMember } from '@/matter/utils/matter-permissions'

/**
 * Select: Matters Record
 *
 * Retrieves the full matters record from the state.
 */
export const selectMattersRecord = (state: RootState): MattersRecord => {
  return state.mattersState.matters
}

/**
 * Select: Matters List
 *
 * Returns a list of MatterRead objects, unsorted.
 */
export const selectMattersList = createSelector(selectMattersRecord, (mattersRecord): MatterRead[] => {
  return Object.values(mattersRecord).filter((matter): matter is MatterRead => !!matter)
})

/**
 * Select: Matters Sorted by Name
 *
 * Returns a list of MatterRead objects, sorted alphabetically by name (case-insensitive).
 */
export const selectMattersSortedByName = createSelector(selectMattersList, (matters): MatterRead[] => {
  return [...matters].sort((a, b) => {
    // Sort alphabetically by name, case-insensitive
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
})

/**
 * Select: Interactive Matters Sorted by Name
 *
 * Returns only matters users can interact with normally, sorted alphabetically by name.
 * Use this for dropdowns where the unassigned matter should not appear as an option.
 */
export const selectInteractiveMattersSortedByName = createSelector(selectMattersSortedByName, (matters): MatterRead[] => {
  return matters.filter((matter) => matter.type === MatterType.STANDARD || matter.type === MatterType.SAMPLE)
})

/**
 * Select: Matter Files Destinations Sorted by Name
 *
 * Returns matters that should be offered as normal Matter Files destinations.
 * The backend remains authoritative; this is only for accepted-member UX.
 */
export const selectMatterFilesMemberMattersSortedByName = createSelector(selectMattersSortedByName, (matters): MatterRead[] => {
  return matters.filter((matter) => {
    if (matter.type !== MatterType.STANDARD && matter.type !== MatterType.SAMPLE) return false
    return isActiveMatterMember(matter)
  })
})

/**
 * Builds a name-sorted, interactive-matter selector gated by a matter permission.
 * One selector per behavior so they can be enabled independently.
 */
const makeSelectMattersSortedByName = (canAccess: (matter: MatterRead) => boolean) =>
  createSelector(selectInteractiveMattersSortedByName, (matters): MatterRead[] => matters.filter(canAccess))

/** Select: matters the viewer can start a new chat in. */
export const selectChatCreateMattersSortedByName = makeSelectMattersSortedByName(canCreateChat)

/** Select: matters the viewer can move or assign an existing chat into. */
export const selectMoveChatDestinationsMattersSortedByName = makeSelectMattersSortedByName(canMoveChatToMatter)

/**
 * Select: Matters List for Table
 *
 * Returns a list of MatterReadForTable objects with null/empty string values transformed to undefined
 * for nullable fields. This allows TanStack Table's `sortUndefined: 'last'` option to work
 * correctly, keeping blank values at the bottom regardless of sort direction.
 */
export const selectMattersListForTable = createSelector(selectMattersList, (matters): MatterReadForTable[] => {
  return matters.map((matter) => ({
    ...matter,
    // Transform null/empty strings to undefined for sortUndefined to work
    client: matter.client == null || matter.client.trim() === '' ? undefined : matter.client,
    description: matter.description == null || matter.description.trim() === '' ? undefined : matter.description,
    last_activity_at: matter.last_activity_at ?? undefined,
  }))
})

/**
 * Select: Sample Matter
 *
 * Returns the first sample matter, if one exists.
 */
export const selectSampleMatter = createSelector(selectMattersSortedByName, (matters): MatterRead | null => {
  return matters.find((matter) => matter.type === MatterType.SAMPLE) ?? null
})

/**
 * Select: Has No Non-Sample Matters
 *
 * Returns true if the user has no non-sample matters.
 */
export const selectHasNoNonSampleMatters = createSelector(selectMattersRecord, (mattersRecord) => {
  return Object.values(mattersRecord).filter((matter) => matter?.type !== MatterType.SAMPLE).length === 0
})

/**
 * Select: Has Only Legacy Unassigned Matter Excluding Samples
 *
 * Returns true if the matters record contains only the legacy unassigned matter,
 * ignoring sample matters surfaced by onboarding empty states.
 */
export const selectHasOnlyLegacyUnassignedMatterExcludingSamples = createSelector(selectMattersRecord, (mattersRecord) => {
  const mattersArray = Object.values(mattersRecord).filter((matter) => matter?.type !== MatterType.SAMPLE)
  if (mattersArray.length !== 1) {
    return false
  }

  return mattersArray[0]?.type === MatterType.LEGACY_UNASSIGNED
})

/**
 * Select: Has Legacy Unassigned Matter
 *
 * Returns true if the user has a LEGACY_UNASSIGNED matter.
 * Use this to determine if the Unassigned folder should be shown.
 */
export const selectHasLegacyUnassignedMatter = createSelector(selectMattersList, (matters): boolean => {
  return matters.some((matter) => matter.type === MatterType.LEGACY_UNASSIGNED)
})

/**
 * Select: Matter by ID
 *
 * Returns the MatterRead object for the given matterId, or null if it does not exist.
 */
export const selectMatterById = createSelector(
  [selectMattersRecord, (_: RootState, props: { matterId: string | undefined | null }) => props.matterId],
  (mattersRecord, matterId): MatterRead | null => {
    if (!matterId) return null
    return mattersRecord[matterId] ?? null
  }
)

/**
 * Select: Rendered Matter Name Map
 *
 * Returns a map of matter ids to their display names - used when we need to display
 * matter names but only have access to matter ids, without the full matter objects.
 */
export const selectRenderedMatterNameMap = createSelector([selectMattersList], (matters) => {
  const map = new Map<string, string>(matters.map((matter) => [matter.id, matter.name ?? kUntitledMatterLabel]))
  return map
})

/**
 * Select: Active Matter ID
 *
 * Retrieves the ID of the currently active matter.
 */
export const selectActiveMatterId = (state: RootState): string | null => {
  return state.mattersState.activeMatterId
}

/**
 * Select: Active Matter
 *
 * Retrieves the full MatterRead object for the currently active matter.
 * Returns null if no matter is active or the active matter is not found.
 */
export const selectActiveMatter = createSelector([selectMattersRecord, selectActiveMatterId], (mattersRecord, activeMatterId): MatterRead | null => {
  if (!activeMatterId) {
    return null
  }
  return mattersRecord[activeMatterId] ?? null
})
